import * as React from 'react';
import { BookOpen, ChevronDown, FileText, Globe, Plus, Search, Send, Upload } from 'lucide-react';
import { WorkspaceButton, useToast } from '@/components/ui/workspace';
import { VendorConfidentialChip } from '@/components/ui/citation-chip';
import {
  errorMessage,
  isMissingRoute,
  useKbImports,
  useKbReceived,
  useKbSources,
  useKbUploadLinks,
  useRevokeUploadLink,
  useSourceAction,
  type KbSourceRow,
  type KbUploadLink,
  type KbVendor,
} from '@/lib/api/vendor-knowledge';
import { cn } from '@/lib/utils/cn';
import { Chip, Empty, Section, SectionHead, SubHead, type ChipTone } from '../kb-bits';
import { formatDate, hostPath, outcomeText, plural, shortDate } from '../kb-logic';
import { ReasonForm } from '../review/flags';
import { useVendorPage, type SourceFormKind } from '../vendor-page-context';
import { SourceDetail } from './source-detail';
import { SourceForm } from './source-forms';

/**
 * Sources (prototype `vSources()`; T-0207, E13-F04/F05): the vendor's web
 * pages and documents with their status in words, its upload links, and
 * the Caliber v1 import. Each source opens in place with its versions,
 * checks and controls. The four ways to add one open as a small form at
 * the top. Captures and links are for curators only (rules 15, 20).
 */
export function SourcesSection({ data }: { data: KbVendor }) {
  const { form, openForm } = useVendorPage();
  const vendorId = data.vendor.id;
  const sources = useKbSources(vendorId);
  const links = useKbUploadLinks(vendorId, true);
  const received = useKbReceived(vendorId, true);
  const imports = useKbImports();
  const [open, setOpen] = React.useState<string | null>(null);

  // An API without the documents or upload-link routes answers 404: those
  // actions are then left out rather than shown broken.
  const canUpload = !isMissingRoute(received.error);
  const canLink = !isMissingRoute(links.error);

  const rows = sources.data?.sources ?? [];
  const web = rows.filter((s) => s.kind === 'web_page');
  const docs = rows.filter((s) => s.kind === 'vendor_document');
  const linkRows = links.data?.links ?? [];
  const imported = imports.data?.runs.find(
    (r) => r.stage === 'records' && r.status === 'completed',
  );

  const action = (kind: SourceFormKind, icon: React.ReactNode, label: string) => (
    <WorkspaceButton
      variant="tertiary"
      aria-expanded={form === kind}
      className={cn(form === kind && 'bg-paper-100')}
      onClick={() => openForm(form === kind ? null : kind)}
    >
      {icon} {label}
    </WorkspaceButton>
  );

  return (
    <Section id="kb-sources" label="Sources">
      <SectionHead
        title="Sources"
        count={web.length + docs.length + (imported ? 1 : 0)}
        actions={
          <>
            {action('web', <Plus className="h-3.5 w-3.5" aria-hidden="true" />, 'Add a web page')}
            {action('find', <Search className="h-3.5 w-3.5" aria-hidden="true" />, 'Find pages')}
            {canUpload
              ? action(
                  'upload',
                  <Upload className="h-3.5 w-3.5" aria-hidden="true" />,
                  'Upload a document',
                )
              : null}
            {canLink
              ? action(
                  'link',
                  <Send className="h-3.5 w-3.5" aria-hidden="true" />,
                  'Send an upload link',
                )
              : null}
          </>
        }
      />
      {form && (form !== 'upload' || canUpload) && (form !== 'link' || canLink) ? (
        <div id="kb-source-form">
          <SourceForm
            kind={form}
            data={data}
            sources={rows}
            onClose={() => openForm(null)}
            onSwitch={(k) => openForm(k)}
          />
        </div>
      ) : null}

      <SubHead count={web.length}>Web pages</SubHead>
      {sources.isPending ? (
        <div className="text-meta text-ink-500">Loading…</div>
      ) : web.length ? (
        <Rows>
          {web.map((s) => (
            <SourceRow
              key={s.id}
              source={s}
              open={open === s.id}
              onToggle={() => setOpen(open === s.id ? null : s.id)}
              data={data}
            />
          ))}
        </Rows>
      ) : (
        <Empty small>No web pages yet.</Empty>
      )}

      <SubHead count={docs.length}>Documents</SubHead>
      {docs.length ? (
        <Rows>
          {docs.map((s) => (
            <SourceRow
              key={s.id}
              source={s}
              open={open === s.id}
              onToggle={() => setOpen(open === s.id ? null : s.id)}
              data={data}
            />
          ))}
        </Rows>
      ) : (
        <Empty small>No documents yet.</Empty>
      )}

      {canLink && linkRows.length > 0 ? (
        <>
          <SubHead count={linkRows.length}>Upload links</SubHead>
          <Rows>
            {linkRows.map((l) => (
              <LinkRow key={l.id} link={l} />
            ))}
          </Rows>
        </>
      ) : null}

      {imported ? (
        <>
          <SubHead>Avero Advisors Vendor Knowledge Base</SubHead>
          <Rows>
            <div className="flex items-center gap-3 px-3.5 py-2.5">
              <BookOpen className="h-4 w-4 flex-none text-ink-500" aria-hidden="true" />
              <div className="min-w-0 flex-1">
                <div className="font-medium text-ink-900">
                  Imported from Caliber v1 on{' '}
                  {formatDate(imported.finished_at ?? imported.started_at)}
                </div>
                <div className="text-meta text-ink-500">
                  Its items and requirement answers are cited as Avero Advisors Vendor Knowledge
                  Base, with the date each entered it.
                </div>
              </div>
            </div>
          </Rows>
        </>
      ) : null}
    </Section>
  );
}

function Rows({ children }: { children: React.ReactNode }) {
  return (
    <div className="overflow-hidden rounded-card border border-ink-100 bg-paper-0 [&>*+*]:border-t [&>*+*]:border-ink-100">
      {children}
    </div>
  );
}

/** A source's status in words (prototype `statusChip()`), from the server's own fields. */
export function sourceStatus(s: KbSourceRow): { label: string; tone: ChipTone; tip: string } {
  if (s.status === 'retired')
    return { label: 'Retired', tone: 'frozen', tip: 'No longer checked or read' };
  if (s.status === 'pending') {
    return {
      label: 'Waiting for its domain',
      tone: 'unassessed',
      tip: 'Captured once its web domain is approved on the vendor’s header',
    };
  }
  if (s.kind === 'vendor_document') {
    return s.waiting_to_read
      ? { label: 'Waiting to be read', tone: 'unassessed', tip: 'Received, not read yet' }
      : { label: 'Read', tone: 'healthy', tip: 'Read in full' };
  }
  if (s.current_version_no === null) {
    return { label: 'Waiting first check', tone: 'unassessed', tip: 'Not captured yet' };
  }
  switch (s.last_outcome) {
    case 'unchanged':
      return {
        label: 'Unchanged',
        tone: 'healthy',
        tip: 'The page still says what the knowledge base has',
      };
    case 'changed':
      return {
        label: 'Changed',
        tone: 'at-risk',
        tip: 'The page differs from the capture before it',
      };
    case 'first_capture':
      return {
        label: 'Captured',
        tone: 'neutral',
        tip: 'Captured once; the next check compares against it',
      };
    case 'not_found':
    case 'gone':
      return { label: 'Gone', tone: 'critical', tip: 'The site says the page doesn’t exist' };
    case 'blocked_robots':
      return {
        label: 'Blocked by robots.txt',
        tone: 'frozen',
        tip: 'The site asks crawlers not to fetch this page, so Caliber doesn’t',
      };
    case 'needs_browser':
      return {
        label: 'Needs a browser',
        tone: 'frozen',
        tip: 'The page shows its text only in a browser. Ask the vendor for a document instead.',
      };
    default: {
      const words = outcomeText(s.last_outcome);
      return {
        label: words.charAt(0).toUpperCase() + words.slice(1),
        tone: s.problem ? 'at-risk' : 'neutral',
        tip: words,
      };
    }
  }
}

function SourceRow({
  source: s,
  open,
  onToggle,
  data,
}: {
  source: KbSourceRow;
  open: boolean;
  onToggle: () => void;
  data: KbVendor;
}) {
  const { toast } = useToast();
  const action = useSourceAction();
  const web = s.kind === 'web_page';
  const status = sourceStatus(s);
  const when = web
    ? s.last_checked_at
      ? `Checked ${shortDate(s.last_checked_at)}${s.next_check_at && s.status === 'active' ? ` · next ${shortDate(s.next_check_at)}` : ''}`
      : 'Not checked yet'
    : s.current_version_no
      ? `Version ${s.current_version_no}`
      : '';
  const notes = [
    s.url ? hostPath(s.url) : null,
    web ? null : (s.product?.name ?? null),
    when || null,
    s.waiting_to_read && web ? 'waiting to be read' : null,
  ].filter(Boolean);
  return (
    <div className={cn(open && 'shadow-[inset_3px_0_0_var(--accent-amber-600)]')}>
      <div className="flex flex-wrap items-center gap-3 px-3.5 py-2.5 sm:flex-nowrap">
        {web ? (
          <Globe className="h-4 w-4 flex-none text-ink-500" aria-hidden="true" />
        ) : (
          <FileText className="h-4 w-4 flex-none text-ink-500" aria-hidden="true" />
        )}
        <button
          type="button"
          aria-expanded={open}
          onClick={onToggle}
          className="min-w-0 flex-1 rounded-sm border-0 bg-transparent p-0 text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent-amber-600"
        >
          <span className="flex min-w-0 items-center gap-2">
            <span className="truncate font-medium text-ink-900">
              {s.title ?? (s.url ? hostPath(s.url) : 'Untitled')}
            </span>
            {s.confidential ? <VendorConfidentialChip /> : null}
          </span>
          <span className="block truncate text-[11px] leading-[18px] text-ink-500">
            {notes.join(' · ')}
          </span>
        </button>
        <Chip tone={status.tone} title={status.tip}>
          {status.label}
        </Chip>
        <span className="flex flex-none items-center gap-3">
          {web && s.status === 'active' ? (
            <button
              type="button"
              disabled={action.isPending}
              onClick={() =>
                action.mutate(
                  { id: s.id, verb: 'check-now' },
                  {
                    onSuccess: (out) =>
                      toast({
                        message:
                          out['created'] === false
                            ? 'A check of this page is already running.'
                            : `Checking ${s.url ? hostPath(s.url) : 'the page'} now. Unchanged pages cost nothing.`,
                      }),
                    onError: (err) => toast({ message: errorMessage(err), tone: 'critical' }),
                  },
                )
              }
              className="text-meta font-medium text-ink-700 underline underline-offset-[3px] hover:text-ink-900 focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent-amber-600"
            >
              Check now
            </button>
          ) : null}
          <ChevronDown
            className={cn('h-3.5 w-3.5 text-ink-300', open && 'rotate-180')}
            aria-hidden="true"
          />
        </span>
      </div>
      {open ? <SourceDetail sourceId={s.id} products={data.products} /> : null}
    </div>
  );
}

function LinkRow({ link: l }: { link: KbUploadLink }) {
  const { toast } = useToast();
  const revoke = useRevokeUploadLink();
  const [revoking, setRevoking] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const status: { label: string; tone: ChipTone } =
    l.status === 'revoked'
      ? { label: 'Revoked', tone: 'frozen' }
      : l.status === 'expired'
        ? { label: 'Expired', tone: 'frozen' }
        : l.files_received > 0
          ? { label: `${plural(l.files_received, 'file')} received`, tone: 'healthy' }
          : { label: 'Sent', tone: 'neutral' };
  return (
    <div className="px-3.5 py-2.5">
      <div className="flex flex-wrap items-center gap-3 sm:flex-nowrap">
        <Send className="h-4 w-4 flex-none text-ink-500" aria-hidden="true" />
        <div className="min-w-0 flex-1">
          <div className="truncate font-medium text-ink-900">
            {l.recipient_email}
            {l.product ? (
              <span className="font-normal text-ink-500"> · {l.product.name}</span>
            ) : null}
          </div>
          <div className="text-meta text-ink-500">
            “{l.purpose}” Sent {shortDate(l.issued_at)}
            {l.issued_by.name ? ` by ${l.issued_by.name}` : ''} ·{' '}
            {l.status === 'revoked'
              ? `revoked ${shortDate(l.revoked_at)}`
              : l.status === 'expired'
                ? `ended ${shortDate(l.expires_at)}`
                : `works until ${formatDate(l.expires_at)}`}
          </div>
        </div>
        <Chip tone={status.tone}>{status.label}</Chip>
        {l.status === 'live' && !revoking ? (
          <button
            type="button"
            onClick={() => setRevoking(true)}
            className="text-meta font-medium text-posture-critical underline underline-offset-[3px] focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent-amber-600"
          >
            Revoke
          </button>
        ) : null}
      </div>
      {revoking ? (
        <ReasonForm
          label="Why revoke this link?"
          placeholder="For example: sent to the wrong person"
          help="The link stops working at once. Files already received stay."
          button="Revoke"
          destructive
          pending={revoke.isPending}
          error={error}
          onCancel={() => setRevoking(false)}
          onSubmit={(reason) =>
            revoke.mutate(
              { id: l.id, reason },
              {
                onSuccess: () => {
                  toast({ message: `Revoked. The link to ${l.recipient_email} no longer opens.` });
                  setRevoking(false);
                },
                onError: (err) => setError(errorMessage(err)),
              },
            )
          }
        />
      ) : null}
    </div>
  );
}
